'use client';

import type { ReactNode } from 'react';
import AsyncView from '../../../../components/AsyncView';
import { debugSSR } from '../../../../consts/debug';
import { WITH_SUSPENSE } from '../../../const';
import type { UseFetchResourcesHookType } from '../../../hooks/fetchAndInvalidation/types';
import { useGlobalAppStateStore } from '../../../stores/app/GlobalAppStateStore';
import { InterfaceModes, type InterfaceStoreState } from '../../../stores/InterfaceStore';
import { InterfacePageProviders } from '../InterfacePageProviders';
import type { InterfacePageProvidersProps } from '../InterfacePageProviders/types';
import WalkthroughClientLoader from './WalkthroughClientLoader';

export type InterfacePageResourceLoaderProps = Pick<
  InterfacePageProvidersProps,
  | 'emitPageEvent'
  | 'interfaceResourceProviderRef'
  | 'isPublic'
  | 'pageInputs'
  | 'pageParams'
  | 'outsideGlobalStateSlot'
> & {
  children: ReactNode;
  disableInteractiveMode?: boolean;
  renderFallback?: () => React.ReactNode;
  renderLoader?: () => React.ReactNode;
  renderError?: (error?: Error) => React.ReactNode;
  mode?: InterfaceStoreState['mode'];
  disableDevTools?: boolean;
  response: ReturnType<UseFetchResourcesHookType>;
};

function InterfacePageResourceLoader({
  children,
  pageInputs,
  pageParams,
  emitPageEvent,
  interfaceResourceProviderRef,
  outsideGlobalStateSlot,
  disableInteractiveMode,
  isPublic,
  renderFallback,
  renderLoader,
  renderError,
  mode = InterfaceModes.RUNNER,
  disableDevTools,
  response,
}: InterfacePageResourceLoaderProps) {
  const interfaceRecord = useGlobalAppStateStore().hooks.record.useDetails();
  const {
    dataSources,
    error,
    interfacePage,
    isLoading,
    interfacePageNamespace,
    namespaces,
    embeddedPages,
    embeddedInterfaceRecords,
    customBlocksSnapshots,
    customBlocks,
  } = response;

  // Derive current page namespace from map if not explicitly provided
  const currentPageNamespace =
    interfacePageNamespace ??
    (interfacePage?.id && namespaces ? namespaces[interfacePage.id] : undefined);

  const interfacePageId = interfacePage?.id;
  const interfaceId = interfaceRecord?.id;

  debugSSR(
    'InterfacePageResourceLoader',
    {
      isLoading,
    },
    {
      interfaceId,
      pageId: interfacePageId,
      extraInfo: {
        hasError: Boolean(error),
        hasInterfaceRecord: Boolean(interfaceRecord),
        hasInterfacePage: Boolean(interfacePage),
      },
    },
  );

  return (
    <>
      <WalkthroughClientLoader isPublic={isPublic} mode={mode} response={response} />
      <AsyncView
        data={interfaceRecord}
        error={error}
        isLoading={isLoading}
        remountOnError={mode === InterfaceModes.RUNNER}
        renderError={renderError ?? renderFallback}
        renderLoader={renderLoader}
        withSuspense={WITH_SUSPENSE}
      >
        {interfaceRecord && interfacePage ? (
          <InterfacePageProviders
            customBlocks={customBlocks}
            customBlocksSnapshots={customBlocksSnapshots}
            dataSources={dataSources}
            disableDevTools={disableDevTools}
            disableInteractiveMode={disableInteractiveMode}
            embeddedInterfacePageNamespaces={namespaces}
            embeddedInterfaceRecords={embeddedInterfaceRecords}
            embeddedPages={embeddedPages}
            emitPageEvent={emitPageEvent}
            interfacePageNamespace={currentPageNamespace}
            interfaceRecord={interfaceRecord}
            interfaceResourceProviderRef={interfaceResourceProviderRef}
            isPublic={isPublic}
            mode={mode}
            outsideGlobalStateSlot={outsideGlobalStateSlot}
            page={interfacePage}
            pageInputs={pageInputs}
            pageParams={pageParams}
          >
            {children}
          </InterfacePageProviders>
        ) : null}
      </AsyncView>
    </>
  );
}

export default InterfacePageResourceLoader;
